/**
 * Architects/Engineers - Dashboard Page
 * Overview metrics for architecture and engineering firms
 */

import { useEffect, useMemo } from 'react';
import { useIndustryProjects } from '@/hooks/industry';
import { CompanyType } from '@/types/company';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Ruler, TrendingUp, DollarSign, FolderOpen, BarChart3, Calendar } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { MainLayout } from '@/components/MainLayout';

export default function ArchitectsEngineersDashboardPage() {
  useEffect(() => {
    document.title = 'Dashboard de Projetos | Controle Financeiro';
  }, []);

  const { projects, isLoading } = useIndustryProjects({ domain: CompanyType.ARCHITECTS_ENGINEERS });

  const stats = useMemo(() => {
    if (!projects) return null;

    const activeProjects = projects.filter((p) => p.status === 'in_progress').length;
    const completedProjects = projects.filter((p) => p.status === 'completed').length;
    const plannedProjects = projects.filter((p) => p.status === 'planned').length;

    const totalBudget = projects.reduce((sum, p) => sum + (p.budget || 0), 0);
    const totalActualCost = projects.reduce((sum, p) => sum + (p.actual_cost || 0), 0);
    const totalProfit = totalBudget - totalActualCost;

    const avgBudget = projects.length > 0 ? totalBudget / projects.length : 0;

    return {
      totalProjects: projects.length,
      activeProjects,
      completedProjects,
      plannedProjects,
      totalBudget,
      totalActualCost,
      totalProfit,
      avgBudget,
      profitMargin: totalBudget > 0 ? (totalProfit / totalBudget) * 100 : 0,
    };
  }, [projects]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  if (isLoading) {
    return (
      <MainLayout>
        <div className="container mx-auto py-6">
          <div className="flex items-center justify-center py-12">
            <div className="text-muted-foreground">Carregando...</div>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Ruler className="h-8 w-8" />
            Dashboard de Projetos
          </h1>
          <p className="text-muted-foreground">Visão geral dos seus projetos de arquitetura e engenharia</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardDescription className="flex items-center gap-2">
              <FolderOpen className="h-4 w-4" />
              Total de Projetos
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.totalProjects || 0}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {stats?.activeProjects || 0} em andamento · {stats?.plannedProjects || 0} planejados
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardDescription className="flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              Orçamento Total
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(stats?.totalBudget || 0)}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Valor total contratado
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardDescription className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              Margem de Lucro
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.profitMargin.toFixed(1)}%</div>
            <p className="text-xs text-muted-foreground mt-1">
              Lucro: {formatCurrency(stats?.totalProfit || 0)}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardDescription className="flex items-center gap-2">
              <BarChart3 className="h-4 w-4" />
              Valor Médio
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(stats?.avgBudget || 0)}</div>
            <p className="text-xs text-muted-foreground mt-1">Orçamento médio por projeto</p>
          </CardContent>
        </Card>
      </div>

      {/* Financial Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5" />
              Receita Prevista
            </CardTitle>
            <CardDescription>Orçamento total contratado</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-green-600">{formatCurrency(stats?.totalBudget || 0)}</div>
            <p className="text-sm text-muted-foreground mt-2">
              {stats?.totalProjects || 0} projetos ativos e planejados
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Custos Realizados
            </CardTitle>
            <CardDescription>Despesas efetivamente realizadas</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-orange-600">{formatCurrency(stats?.totalActualCost || 0)}</div>
            <p className="text-sm text-muted-foreground mt-2">
              {stats?.completedProjects || 0} projetos concluídos
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader>
            <CardTitle>Projetos</CardTitle>
            <CardDescription>Gerencie seu portfólio de projetos</CardDescription>
          </CardHeader>
          <CardContent>
            <Link to="/architects-engineers/projects">
              <Button className="w-full">
                <FolderOpen className="mr-2 h-4 w-4" />
                Gerenciar Projetos
              </Button>
            </Link>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Timeline</CardTitle>
            <CardDescription>Cronogramas e etapas dos projetos</CardDescription>
          </CardHeader>
          <CardContent>
            <Link to="/architects-engineers/projects">
              <Button className="w-full" variant="outline">
                <Calendar className="mr-2 h-4 w-4" />
                Ver Cronogramas
              </Button>
            </Link>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Relatórios</CardTitle>
            <CardDescription>Análise financeira e lucratividade</CardDescription>
          </CardHeader>
          <CardContent>
            <Link to="/architects-engineers/profitability">
              <Button className="w-full" variant="outline">
                <BarChart3 className="mr-2 h-4 w-4" />
                Ver Relatório
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    </div>
    </MainLayout>
  );
}
